import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from '../../styles/components/modules/food-budget-module.module.css';

interface FoodBudgetButton {
  id: string;
  label: string;
  hasAlert: boolean;
  alertCount?: number;
  isActive: boolean;
  warningCode?: string; // 对应的预警代码
}

interface FoodBudgetModuleProps {
  onButtonClick?: (buttonId: string) => void;
  onWarningClick?: () => void;
  warningCount?: Record<string, number>;
}

const FoodBudgetModule: React.FC<FoodBudgetModuleProps> = ({ 
  onButtonClick,
  onWarningClick,
  warningCount
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 处理预警标准按钮点击
  const handleWarningClick = () => {
    const params = new URLSearchParams();
    const deptId = searchParams.get('deptId');
    const identity = searchParams.get('identity');
    const deptName = searchParams.get('deptName');
    
    if (deptId) params.set('deptId', deptId);
    if (identity) params.set('identity', identity);
    if (deptName) params.set('deptName', deptName);
    params.set('module', 'budget');
    
    navigate(`/warning-standard?${params.toString()}`);
  };
  // 膳食经费模块按钮配置，根据预警接口文档映射预警代码
  const getButtons = (): FoodBudgetButton[] => {
    const baseButtons: Omit<FoodBudgetButton, 'hasAlert' | 'alertCount'>[] = [
      { id: 'canteen_account', label: '食堂建账', isActive: true, warningCode: 'CODE0010001' },
      { id: 'fund_source', label: '资金来源', isActive: true, warningCode: 'CODE0010002' },
      { id: 'canteen_balance', label: '食堂结余', isActive: true, warningCode: 'CODE0010003' },
      { id: 'meal_subsidy', label: '餐费补助', isActive: true, warningCode: 'CODE0010004' },
      { id: 'price_compare', label: '菜价对比', isActive: true, warningCode: 'CODE0010005' },
      { id: 'refund_balance', label: '余额退还', isActive: true, warningCode: 'CODE0010006' },
      { id: 'ingredient_ratio', label: '食材≥70%', isActive: true, warningCode: 'CODE0010007' },
      { id: 'finance_public', label: '财务公开', isActive: true, warningCode: 'CODE0010008' },
      { id: 'price_public', label: '菜价公开', isActive: true, warningCode: 'CODE0010009' },
      { id: 'salary_payment', label: '工资发放', isActive: true, warningCode: 'CODE0010010' },
      { id: 'staff_config', label: '人员配置', isActive: true, warningCode: 'CODE0010011' },
      { id: 'fund_system', label: '经费制度', isActive: true, warningCode: 'CODE0010012' },
      { id: 'same_meal_price', label: '同餐同价', isActive: true, warningCode: 'CODE0010013' }
    ];

    return baseButtons.map(button => {
      const alertCount = warningCount?.[button.warningCode!] || 0;
      return {
        ...button,
        hasAlert: alertCount > 0,
        alertCount: alertCount > 0 ? alertCount : undefined
      };
    });
  };

  const buttons = getButtons();

  // 处理功能按钮点击
  const handleButtonClick = (button: FoodBudgetButton) => {
    const params = new URLSearchParams();
    const deptId = searchParams.get('deptId');
    const identity = searchParams.get('identity');
    const deptName = searchParams.get('deptName');
    
    if (deptId) params.set('deptId', deptId);
    if (identity) params.set('identity', identity);
    if (deptName) params.set('deptName', deptName);
    if (button.warningCode) params.set('warningCode', button.warningCode);
    
    navigate(`/warning-detail?${params.toString()}`);
  };

  const renderButton = (button: FoodBudgetButton) => {
    return (
      <div
        key={button.id}
        className={`${styles.functionButton} ${
          button.hasAlert ? styles.alertButton : styles.normalButton
        }`}
        onClick={() => handleButtonClick(button)}
      >
        <span className={styles.buttonText}>{button.label}</span>
        {button.hasAlert && (
          <div className={styles.alertBadge}>
            {button.alertCount || 1}
          </div>
        )}
      </div>
    );
  };

  // 按每行3个按钮分组
  const buttonRows = [];
  for (let i = 0; i < buttons.length; i += 3) {
    buttonRows.push(buttons.slice(i, i + 3));
  }

  return (
    <div className={styles.foodBudgetModule}>
      {/* 标题区域 - 使用bg-title.png */}
      <div className={styles.moduleHeader}>
        <h3 className={styles.moduleTitle}>膳食经费</h3>
      </div>
      
      {/* 内容区域 - 使用bg-content.png */}
      <div className={styles.contentContainer}>
        {/* 预警标准按钮 */}
        <div className={styles.warningSection}>
          <button
            className={styles.warningButton}
            onClick={handleWarningClick}
          >
            预警标准 &gt;
          </button>
        </div>
        
        {/* 按钮容器 */}
        <div className={styles.buttonsContainer}>
          {buttonRows.map((row, rowIndex) => (
            <div key={rowIndex} className={styles.buttonRow}>
              {row.map(renderButton)}
              {/* 填充空白位置保持对齐 */}
              {row.length < 3 && 
                Array.from({ length: 3 - row.length }, (_, i) => (
                  <div key={`empty-${i}`} className={styles.emptySlot} />
                ))
              }
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FoodBudgetModule;